import {
  VALIDATION_REGEX_KATAKANA,
  VALIDATION_REGEX_PHONE_NUMBER,
  VALIDATION_REGEX_ZIP_CODE,
} from "@/shared/lib/validation";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseCheckbox } from "@/shared/ui/base-checkbox";
import { BaseLink } from "@/shared/ui/base-link";
import { BaseSelect } from "@/shared/ui/base-select";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgress, MenuItem, Stack, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { AccountRequestCreatePageProps } from "../model";

const accountRequestFormSchema = z.object({
  address: z.string().min(1, "入力が必要です"),
  companyCategory: z.enum(["carrier", "shipper"], {
    required_error: "企業種別を選択してください",
  }),
  companyName: z.string().min(1, "入力が必要です"),
  officeName: z.string(),
  email: z
    .string()
    .min(1, "入力が必要です")
    .email("正しい形式のメールアドレスを入力してください"),
  firstName: z.string().min(1, "入力が必要です"),
  firstNameKana: z
    .string()
    .min(1, "入力が必要です")
    .regex(VALIDATION_REGEX_KATAKANA, "全角カタカナで入力してください"),
  lastName: z.string().min(1, "入力が必要です"),
  lastNameKana: z
    .string()
    .min(1, "入力が必要です")
    .regex(VALIDATION_REGEX_KATAKANA, "全角カタカナで入力してください"),
  phoneNumber: z
    .string()
    .regex(
      VALIDATION_REGEX_PHONE_NUMBER,
      "電話番号は 10桁または11桁の数字のみで入力してください",
    ),
  zipCode: z
    .string()
    .regex(
      VALIDATION_REGEX_ZIP_CODE,
      "郵便番号は 123-4567 の形式で入力してください",
    ),
  termsApproved: z.boolean().refine((val) => val === true, {
    message: "利用規約に同意する必要があります",
  }),
});

type FormValues = z.infer<typeof accountRequestFormSchema>;
type AccountRequestFormProps = {
  requestStatus: AccountRequestCreatePageProps["requestStatus"];
  errorMessage: AccountRequestCreatePageProps["errorMessage"];
  onSubmit: AccountRequestCreatePageProps["onAccountRequestSubmit"];
};
export const AccountRequestForm: React.FC<AccountRequestFormProps> = ({
  requestStatus,
  errorMessage,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(accountRequestFormSchema),
    defaultValues: {
      address: "",
      companyCategory: "shipper",
      companyName: "",
      email: "",
      firstName: "",
      firstNameKana: "",
      lastName: "",
      lastNameKana: "",
      officeName: "",
      phoneNumber: "",
      zipCode: "",
    },
    mode: "onChange",
  });

  const watchedCompanyCategory = watch("companyCategory");

  const getTermsUrl = (category: "shipper" | "carrier") => {
    return category === "shipper"
      ? "https://logipoke.com/term2"
      : "https://logipoke.com/term3";
  };

  const handleFormSubmit = (data: FormValues) => {
    onSubmit(data);
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      style={{
        maxWidth: 640,
      }}
    >
      <Stack spacing={3} alignItems="stretch">
        <Stack spacing={2} alignItems="stretch">
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t("accountRequest.companyCategory")}
            </Typography>
            <Controller
              name="companyCategory"
              control={control}
              render={({ field, fieldState }) => (
                <BaseSelect
                  type="string"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.invalid}
                  helperText={errors.companyCategory?.message}
                  size="small"
                >
                  <MenuItem value="shipper">{t("shipper")}</MenuItem>
                  <MenuItem value="carrier">{t("carrier")}</MenuItem>
                </BaseSelect>
              )}
            />
          </Stack>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => (
              <BaseTextField
                {...field}
                label={t("accountRequest.companyName")}
                fullWidth
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
              />
            )}
          />
          <Controller
            name="officeName"
            control={control}
            render={({ field }) => (
              <BaseTextField
                {...field}
                label={`${t("accountRequest.officeName")} (任意)`}
                fullWidth
                error={!!errors.officeName}
                helperText={errors.officeName?.message}
              />
            )}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.zipCode")}
                  fullWidth
                  error={!!errors.zipCode}
                  helperText={errors.zipCode?.message || "例: 123-4567"}
                  sx={{ maxWidth: 172 }}
                />
              )}
            />
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.address")}
                  fullWidth
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body1">ご担当者様</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.lastName")}
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="lastNameKana"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.lastNameKana")}
                  fullWidth
                  error={!!errors.lastNameKana}
                  helperText={errors.lastNameKana?.message}
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.firstName")}
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="firstNameKana"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.firstNameKana")}
                  fullWidth
                  error={!!errors.firstNameKana}
                  helperText={errors.firstNameKana?.message}
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.email")}
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <BaseTextField
                  {...field}
                  label={t("accountRequest.phoneNumber")}
                  fullWidth
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Controller
              name="termsApproved"
              control={control}
              render={({ field }) => (
                <BaseCheckbox
                  variant="plain"
                  {...field}
                  error={!!errors.termsApproved}
                  helperText={errors.termsApproved?.message}
                  label={
                    <Typography variant="body2">
                      <BaseLink href={getTermsUrl(watchedCompanyCategory)}>
                        利用規約
                      </BaseLink>
                      に同意する
                    </Typography>
                  }
                />
              )}
            />
          </Stack>
          <BaseButton
            disabled={!isValid}
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
          >
            {requestStatus !== "submitting" ? (
              "登録申請を送る"
            ) : (
              <CircularProgress size={24} sx={{ color: "white" }} />
            )}
          </BaseButton>
          {errorMessage && (
            <Typography color="error" mt={1}>
              {errorMessage}
            </Typography>
          )}
        </Stack>
      </Stack>
    </form>
  );
};
